import { useState } from "react";
import { X, Search } from "lucide-react";
import { useCurrentCase } from "@/store/appStore";
import { EmptyState, Button } from "@/components/ui/primitives";
import { dispatchAction } from "@/shell/actions";
import { Inspector } from "@/panels/Inspector";
import { ToolWorkspace } from "@/tabs/ToolWorkspace";
import { TOOLS, toolById, TOOL_TABS } from "@/tools/catalog";
import { useToolSession } from "@/tools/session";
import { selectTool } from "@/tools/actions";

function toolKindLabel(t: (typeof TOOLS)[number]): string {
  if (t.status !== "ready") return "Unavailable";
  if (t.stage) return "Protocol";
  if (t.tab === "quantification" || t.inspect === "results") return "Quantify";
  if (t.op) return "Image tool";
  return "Inspect";
}

/** Tools are contextual to the open study; the viewer never leaves the centre. */
export function StudyInspector() {
  const currentCase = useCurrentCase();
  const selectedId = useToolSession((s) => s.selectedId);
  const tool = toolById(selectedId ?? "");
  const [search, setSearch] = useState("");
  const [showTools, setShowTools] = useState(false);
  const matches = TOOLS.filter((t) => `${t.label} ${t.group} ${t.tab}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-ads-border px-3 py-2">
        <span className="text-xs font-semibold">{tool ? tool.label : "Study inspector"}</span>
        <div className="flex items-center gap-2">
          <button type="button" className="text-xs text-ads-accent" onClick={() => setShowTools(!showTools)}>{showTools ? "Hide tools" : "Choose tool"}</button>
          {tool ? <button type="button" aria-label="Close tool and show properties" onClick={() => useToolSession.setState({ selectedId: null })}><X size={14} /></button> : null}
        </div>
      </div>
      {showTools ? <div className="shrink-0 border-b border-ads-border p-2">
        <label className="flex items-center gap-2 rounded border border-ads-border px-2"><Search size={13} /><input aria-label="Search imaging tools" placeholder="Search imaging tools…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-transparent py-2 text-xs outline-none" /></label>
        <div className="max-h-64 overflow-auto py-1">
          {TOOL_TABS.map((tab) => {
            const entries = matches.filter((t) => t.tab === tab.id);
            if (!entries.length) return null;
            return <div key={tab.id}><div className="px-2 pt-2 text-[10px] font-semibold uppercase text-ads-muted">{tab.label}</div>{entries.map((t) => <button key={t.id} type="button" disabled={t.status !== "ready"} title={t.comingSoon ?? t.description} onClick={() => { selectTool(t.id); setShowTools(false); }} className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-ads-hover disabled:text-ads-faint">{t.label}<span className="float-right text-[10px] text-ads-muted">{toolKindLabel(t)}</span></button>)}</div>;
          })}
          {!matches.length ? <p className="p-2 text-xs text-ads-muted">No matching tools.</p> : null}
        </div>
      </div> : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        {tool && tool.tab ? (
          // A tool with no `tab` (SAM/MedSAM) has no per-tab fields page —
          // its session runs in `InteractiveSegPanel`'s own overlay instead.
          <ToolWorkspace tab={tool.tab} />
        ) : currentCase ? (
          <Inspector />
        ) : (
          <div className="p-3">
            <EmptyState title="No study selected" hint="Open a study to inspect image properties." />
            <Button onClick={() => dispatchAction("home.openCase")}>Open study…</Button>
          </div>
        )}
      </div>
    </div>
  );
}
